/**
 * Снятие показанных системных уведомлений (CLIENT_WEB.md §8.5 п. 4–5).
 *
 * Значение `collapse_key` берётся ИЗ РЕПЛИКИ `notifications`, а не собирается
 * по шаблону: формат `conversation:<id>` задан BS §13.1 только для сообщений
 * и может измениться. Для записей без `collapse_key` действует то же правило
 * построения tag, что и в service worker'е (§8.3 п. 5).
 */
import { notificationsRepo } from '@shared/db'

/** Правило §8.3 п. 5 — единственный источник значения `tag`. */
export function notificationTag(notification: { id: string; collapse_key?: string | null }): string {
  return notification.collapse_key ?? `notification:${notification.id}`
}

/**
 * §8.5 п. 4: открытие диалога снимает показанные уведомления его серии.
 * Учитываются только непрочитанные записи — прочитанные уже сняты.
 */
export async function dismissConversationNotifications(conversationId: string): Promise<void> {
  const tags = await tagsForConversation(conversationId)
  await closeByTags(tags)
}

/**
 * §8.5 п. 5: по событиям `block.created`, `user.unavailable`, `match.closed`
 * снимаются уведомления этой пары — и по диалогу, и по match.
 */
export async function dismissPairNotifications(target: {
  conversationId?: string | null
  matchId?: string | null
  userId?: string | null
}): Promise<void> {
  const notifications = await notificationsRepo.list(500)

  const tags = notifications
    .filter((notification) => {
      const payload = notification.payload as Record<string, unknown>

      if (target.conversationId && payload['conversation_id'] === target.conversationId) return true
      if (
        target.matchId &&
        (payload['match_id'] === target.matchId || notification.entity_id === target.matchId)
      )
        return true
      if (target.userId) {
        const counterpart = (payload['counterpart'] ?? payload['sender'] ?? payload['actor']) as
          | { user_id?: string }
          | undefined
        if (counterpart?.user_id === target.userId) return true
      }

      return false
    })
    .map(notificationTag)

  await closeByTags(tags)
}

async function tagsForConversation(conversationId: string): Promise<string[]> {
  const notifications = await notificationsRepo.list(500)

  return notifications
    .filter((notification) => {
      const payload = notification.payload as Record<string, unknown>
      return payload['conversation_id'] === conversationId
    })
    .map(notificationTag)
}

/** Уведомления закрывает service worker: у страницы нет доступа к их объектам. */
async function closeByTags(tags: readonly string[]): Promise<void> {
  const unique = [...new Set(tags)]
  if (!unique.length) return

  const registration = await navigator.serviceWorker?.getRegistration()
  if (!registration) return

  // Прямой путь: страница того же origin может закрыть уведомления сама.
  try {
    for (const tag of unique) {
      const shown = await registration.getNotifications({ tag })
      for (const notification of shown) notification.close()
    }
  } catch {
    // `getNotifications` недоступен — просим worker'а (у него те же права).
    registration.active?.postMessage({ type: 'NOTIFICATIONS_DISMISS', tags: unique })
  }
}
